import { Fragment, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { Link } from 'react-router';
import { formatMessage } from 'i18n/CurrentLocale';
import { FormattedMessage } from 'react-intl';
import { checkIfCompanyHasAppAccount, filterContains, getColumnFilter, getRepresentiveColorForPercentage, stripStringFromNonAlphaNumericCharacters } from 'myParkdUtils/utils';
import { ModalEnum } from 'myParkdUtils/enums';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCar, faColumns, faEnvelope, } from '@fortawesome/free-solid-svg-icons';
import * as modalWindowActions from 'root/modal/modalWindowActions';
import * as vehiclesActions from 'features/vehicles/vehiclesActions';
import * as telematicAccountsActions from 'features/telematics/telematicAccountsActions';
import AppWrapper from 'components/board/AppWrapper';
import NotificationBox from 'components/NotificationBox';
import ReactTableLoadingComponent from 'components/ReactTableLoadingComponent';
import ReactTableNoDataComponent from 'components/ReactTableNoDataComponent';
import GlobalErrorBoundary from 'components/GlobalErrorBoundary';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import { ReduxState, Vehicle } from 'types';
import { getCompanyVehicleColumns, OPTIONAL_VEHICLES_TABLE_COLUMNS } from 'components/vehicles/VehiclesTable';
import { useDebounce } from 'myParkdUtils/customHooks';
import { VehicleFilterProperty, VehiclePredefinedFilter } from 'features/vehicles/companyVehicles';
import MainHeader from 'components/board/MainHeader';
import MainContent from 'components/board/MainContent';
import { getMyParkdUserSettings } from 'features/settings/myParkdSettings';
import { ColumnFilter } from '@tanstack/react-table';
import { isNotEmpty } from 'myParkdUtils/validationExpressions';


export const createRowClassNames = (vehicle: Vehicle): string => {
    let rowClassNames = '';
    if (vehicle.state === 'DISABLED') {
        rowClassNames += ' row-disabled';
    }
    if (vehicle.state === 'BLOCKED' || vehicle.permission === 'BLOCKED_LP') {
        rowClassNames += ' row-blocked';
    }
    if (vehicle.claimState === 'CLAIMED') {
        rowClassNames += ' row-claimed';
    }
    if (vehicle.claimState === 'UNCLAIMED') {
        rowClassNames += ' row-unclaimed';
    }
    if (vehicle.claimState === 'UNCLAIMABLE') {
        rowClassNames += ' row-unclaimable';
    }

    return rowClassNames;
};


export const updateColumnFilters = (filters: ColumnFilter[], id: string, value: string) => {
    let updatedFilters;
    const filterExists = filters.some(f => f.id === id);
    if (filterExists) {
        updatedFilters = filters.map(f => {
            if (f.id === id) {
                return { ...f, value };
            }
            return f;
        });
    } else {
        updatedFilters = [...filters, { id: id, value: value }]
    }
    return updatedFilters.filter(cf => isNotEmpty(cf.value));
}


export const VehiclesScreen = (props: Props) => {
    let vehiclesTable = useRef(null);

    const [licensePlateFilter, setLicensePlateFilter] = useState('');
    const debouncedLicenseplateFilter = useDebounce(licensePlateFilter, 500);

    const onLicensePlateFilterChange = (filterValue: string) => {
        const filteredLicensePlate = stripStringFromNonAlphaNumericCharacters(filterValue).toUpperCase();
        setLicensePlateFilter(filteredLicensePlate);
    }

    const { vehiclesTableColumnSettings = [] } = props?.myParkdUserSettings;
    const { userVehicles } = props.companyVehicles;
    const { blockedTotal, unclaimedTotal, noLocationUpdatesInLast30DaysTotal } = props.companyVehicles.notificationTotals;
    const companyHasVehicles = !!(userVehicles && userVehicles.length);

    const companyHasAppAccount = checkIfCompanyHasAppAccount(props.companyTelematicAccounts.accounts);
    const claimedTotal = userVehicles.length - unclaimedTotal;
    const unclaimedVehiclesPercentage = (claimedTotal / userVehicles.length) * 100;
    const companyVehicleColumns = getCompanyVehicleColumns(vehiclesTableColumnSettings, licensePlateFilter, onLicensePlateFilterChange);

    useEffect(() => {
        props.getCompanyTelematicAccounts();
    }, []);

    useEffect(() => {
        vehiclesTable?.fireFetchData();
    }, [debouncedLicenseplateFilter]);

    const fetchData = (state: any) => {
        const filters: ColumnFilter[] = [
            { id: 'filter', value: getColumnFilter(props.companyVehicles.filters, 'filer')?.value as VehiclePredefinedFilter },
            { id: 'licensePlate', value: licensePlateFilter }
        ].filter(cf => isNotEmpty(cf.value));

        const paging = {
            currentPage: state.page,
            pageSize: state.pageSize,
        };
        const sorting = state.sorted;
        props.updateCompanyVehicles(filters, paging, sorting);
    };

    const updateFilterProperty = (id: VehicleFilterProperty, value: string) => {
        const filters = updateColumnFilters(props.companyVehicles.filters, id, value);
        const paging = {
            currentPage: 0,
            pageSize: vehiclesTable?.state?.pageSize
        };
        const sorting = vehiclesTable?.state?.sorted
        props.updateCompanyVehicles(filters, paging, sorting);
    };

    const hasError = !!props.companyVehicles.error;

    return (
        <AppWrapper>
            <main id="page-vehicles" className="col-main">

                <MainHeader>
                    <div className="header-v-align">
                        <h2 className="main-header-title">
                            <FormattedMessage id="MANAGE_VEHICLES" />
                            &nbsp;
                            {!!props.companyVehicles?.paging?.totalElements && `(${props.companyVehicles?.paging?.totalElements})`}
                        </h2>

                        <div className='header-action-buttons'>

                            <button className='btn-small-blue'
                                onClick={() => props.updateModal(ModalEnum.MANAGE_TABLE_COLUMNS, {
                                    tableSettingsName: 'vehiclesTableColumnSettings',
                                    optionalColumns: OPTIONAL_VEHICLES_TABLE_COLUMNS
                                })}
                            >
                                <FontAwesomeIcon icon={faColumns} className='fa-icon' />
                                <FormattedMessage id="GLOBAL_MANAGE_COLUMNS" />
                            </button>

                            {companyHasAppAccount && (
                                <Link to="/vehicles/import-license-plates" className="btn-small-blue">
                                    <FontAwesomeIcon icon={faCar} className="fa-icon" />
                                    <FormattedMessage id="ADD_VEHICLES" />
                                </Link>
                            )}

                            {companyHasVehicles && (
                                <button
                                    className="btn-small-blue invite-drivers"
                                    onClick={() => props.updateModal(ModalEnum.INVITE_YOUR_DRIVERS)}
                                >
                                    <FontAwesomeIcon icon={faEnvelope} className="fa-icon" />
                                    <FormattedMessage id="INVITE_YOUR_DRIVERS" />
                                </button>
                            )}
                        </div>
                    </div>
                </MainHeader>

                <MainContent>

                    <div className='notification-boxes-wrapper'>
                        <NotificationBox
                            icon={faCar}
                            filter='blocked'
                            activeFilter={getColumnFilter(props.companyVehicles.filters, 'filter')?.value as VehiclePredefinedFilter}
                            message={<FormattedMessage id="VEHICLES_NOTIFICATION_VEHICLES_BLOCKED" values={{ totalVehicles: blockedTotal }} />}
                            totalVehicles={blockedTotal}
                            onToggleButton={(id: VehicleFilterProperty, value: string) => updateFilterProperty(id, value)}
                        />

                        <NotificationBox
                            icon={faCar}
                            filter='unclaimed'
                            activeFilter={getColumnFilter(props.companyVehicles.filters, 'filter')?.value as VehiclePredefinedFilter}
                            message={
                                <Fragment>
                                    <FormattedMessage id="VEHICLES_NOTIFICATION_UNCLAIMED_VEHICLES" values={{ totalVehicles: unclaimedTotal }} />
                                    <div
                                        className="progress-bar"
                                        style={{
                                            display: 'none',
                                            width: unclaimedVehiclesPercentage + '%',
                                            background: getRepresentiveColorForPercentage(unclaimedVehiclesPercentage),
                                        }}
                                    />
                                </Fragment>
                            }
                            totalVehicles={unclaimedTotal}
                            onToggleButton={(id: VehicleFilterProperty, value: string) => updateFilterProperty(id, value)}
                        />
                    </div>

                    {
                        // https://concar.atlassian.net/browse/PD-3708
                        /*
                        <NotificationBox
                            filter='nolocationupdates'
                            activeFilter={props.companyVehicles.filters.filter}
                            message={
                                <FormattedMessage
                                    id="VEHICLES_NOTIFICATION_VEHICLES_WITHOUT_LOCATION_UPDATES"
                                    values={{ totalVehicles: noLocationUpdatesInLast30DaysTotal }}
                                />
                            }
                            totalVehicles={noLocationUpdatesInLast30DaysTotal}
                            onToggleButton={(id: VehicleFilterProperty, value: string) => updateFilterProperty(id, value)}
                        />
                        */
                    }

                    <GlobalErrorBoundary className="eb-red-background eb-rounded-corners">
                        <div className="board-content radius-rectangle no-overflow">
                            <ReactTable
                                className='parkd-react-table'
                                data={props.companyVehicles.isLoading || props.telematicTypeMetadataStatus.loading ? [] : userVehicles}
                                filterable={false}
                                resizable={true}
                                defaultFilterMethod={filterContains}
                                resolveData={(data: any) => data.map((row: any) => row)}
                                columns={companyVehicleColumns}
                                minRows={0}
                                showPagination={props.companyVehicles?.paging?.totalPages > 1}
                                manual={true}
                                onFetchData={fetchData}
                                pages={props.companyVehicles.paging.totalPages}
                                pageSizeOptions={[50, 100, 200]}
                                defaultPageSize={50}
                                defaultSorted={[{ id: 'licensePlate', desc: false }]}
                                loading={props.companyVehicles.isLoading || props.telematicTypeMetadataStatus.loading}
                                LoadingComponent={ReactTableLoadingComponent}
                                noDataText={props.companyVehicles.isLoading || props.telematicTypeMetadataStatus.loading ? '' : formatMessage({ id: 'REACT_TABLE_NO_DATA_VEHICLES' })}
                                NoDataComponent={(props) => (<ReactTableNoDataComponent {...props} error={hasError} />)}
                                previousText={formatMessage({ id: 'REACT_TABLE_PREVIOUS' })}
                                nextText={formatMessage({ id: 'REACT_TABLE_NEXT' })}
                                loadingText={formatMessage({ id: 'REACT_TABLE_LOADING' })}
                                pageText={formatMessage({ id: 'REACT_TABLE_PAGE' })}
                                ofText={formatMessage({ id: 'REACT_TABLE_OF' })}
                                rowsText={formatMessage({ id: 'REACT_TABLE_VEHICLE_ROWS' })}
                                getTrProps={(state: any, row: any) => {
                                    if (row) {
                                        return {
                                            className: createRowClassNames(row.original.vehicle),
                                        };
                                    } else {
                                        return {};
                                    }
                                }}
                                ref={(instance: any) => {
                                    vehiclesTable = instance;
                                }}
                            />
                        </div>
                    </GlobalErrorBoundary>
                </MainContent>

            </main>
        </AppWrapper >
    );
};


type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = DispatchProps & StateProps;


function mapStateToProps(state: ReduxState) {
    return {
        companyVehicles: state.companyVehicles,
        companyTelematicAccounts: state.companyTelematicAccounts,
        telematicTypeMetadataStatus: state.companyDetails.telematicTypeMetadataStatus,
        myParkdUserSettings: getMyParkdUserSettings(state)
    }
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return bindActionCreators(
        {
            updateModal: modalWindowActions.updateModalWindow,
            updateCompanyVehicles: vehiclesActions.updateCompanyVehicles,
            updateFilterProperty: vehiclesActions.updateFilterProperty,
            getCompanyTelematicAccounts: telematicAccountsActions.getCompanyTelematicAccounts,
        },
        dispatch
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(VehiclesScreen);